/**
 * `<MechBuildSheet>` — the MECH page's sheet (`decisions/0206`): the combat unit sheet's parts
 * ({@link UnitSheet}, {@link UnitSheetVitals}, {@link StatStrip}, {@link UnitLoadout}) in their BUILD
 * posture, fed by {@link mechBuildView} from the roster build instead of a spawned combatant. The
 * owner's page, the GM's (opened from ROSTER) and another player's SQUAD view all render it.
 *
 * **Structurally read-only.** Nothing here dispatches; the two capabilities a host may grant are
 * required props, so every call site has to choose (the retired `BrutalistMechSheet`'s rule):
 * - `onCommitNotes`: non-null mounts the NOTES textarea (the owner's page); `null` prints the notes.
 * - `wear`: the owner's or the GM's {@link MechWearBlock} (the Rest controls, which bring their own
 *   dispatch), and with it the carried wear in the vitals; `null` shows maxima only, because
 *   `SHARED_SHEET_DISCLOSURE.printedMechs` is hidden from another player (`SquadApp.tsx`).
 * - `edit`: an editing host's dispatch, undo scope and EDIT switch. Non-null mounts the EDIT
 *   button and, while EDIT is on, the controls of {@link useMechSheetEdit} in place on the same
 *   sheet (`decisions/0206` point 4); `null` (SQUAD) has no EDIT button at all.
 */
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import type { ReactElement, ReactNode } from "react";
import { resolveActiveMechId, type MechData, type PilotData } from "@lancer-vtt/domain";
import type { ComputedPilot } from "@lancer-vtt/rules";
import { listManufacturers } from "@lancer-vtt/compendium";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import { useFictionOnly } from "../../lib/fictionOnly";
import { Icon } from "../../lib/Icon";
import { resolveMechArtUrl } from "../../lib/mechArt";
import { UnitSheet } from "../../lib/encounter/combatWindows/UnitSheet";
import { UnitLoadout } from "../../lib/encounter/combatWindows/UnitLoadout";
import { StatStrip } from "../../lib/encounter/combatWindows/unitStats";
import { UnitSheetVitals } from "../../lib/encounter/combatWindows/unitVitals";
import { mechBuildView } from "../../lib/encounter/unitBuildView";
import type { Ref } from "../compendium/compendiumData";
import type { PreselectBonus } from "./DiceApp";
import { AvatarImage } from "./PilotAvatar";
import { useMechSheetEdit, type MechSheetEdit } from "./MechSheetEdit";
import { SheetNotes, SheetSection } from "./PilotBuildSheet";

const MFR_NAME = new Map(listManufacturers().map((m) => [m.id, m.name]));

export function MechBuildSheet({
  pilot,
  mech,
  computed,
  playerName,
  onOpenInCompendium,
  onCommitNotes,
  onRoll,
  wear,
  band,
  actions,
  edit,
}: {
  pilot: PilotData;
  mech: MechData;
  /** The pilot's computed view (its `mechs` and `grit`); `undefined` until the roster computes. */
  computed: ComputedPilot | undefined;
  /** The owning player's name for the kind line, when the host knows it. */
  playerName?: string;
  onOpenInCompendium: (ref: Ref) => void;
  /** The NOTES write callback: the owner's page only. See the module docstring. */
  onCommitNotes: ((value: string) => void) | null;
  /** Roll buttons on GRIT and HASE (the owner's page only); `null` renders none. */
  onRoll: ((preselect: PreselectBonus) => void) | null;
  /** The WEAR & REPAIRS block (owner and GM); `null` for another player's view. */
  wear: ReactElement | null;
  /** A band across the sheet's top: the MECH page's NOT YOUR ACTIVE MECH · MAKE ACTIVE. The editing host's prototype re-roll prompt stacks under it. */
  band?: ReactNode;
  /** The host's header controls (HANGAR). Read-only hosts pass none. */
  actions?: ReactNode;
  /** The editing capability: see the module docstring. */
  edit: MechSheetEdit | null;
}) {
  const { compendium } = useCompendiumContext();
  const fictionOnly = useFictionOnly();
  const view = mechBuildView({
    pilot,
    mech,
    computed: computed?.mechs.find((m) => m.id === mech.id),
    grit: computed?.grit ?? Math.ceil(pilot.level / 2),
    compendium,
    wear: wear !== null,
    fictionOnly,
  });

  const frame = mech.frameId ? compendium.getById("frame", mech.frameId) : undefined;
  const frameName = frame?.name ?? mech.frameId ?? undefined;
  const mfr = frame?.source ? (MFR_NAME.get(frame.source) ?? frame.source) : undefined;
  const artUrl = resolveMechArtUrl(mech);
  const isActive = resolveActiveMechId(pilot) === mech.id;
  const editing = useMechSheetEdit({ edit, pilot, mech, computed, compendium, onOpenInCompendium });
  // EDIT shows SYSTEMS on a framed mech with none installed, so ＋ INSTALL SYSTEM has a home.
  const viewGroups = editing.groupFoot && mech.frameId && !view.groups.some((g) => g.id === "systems")
    ? [...view.groups.filter((g) => g.id === "weapons"), { id: "systems", title: "SYSTEMS", rows: [], empty: "— none installed —" }, ...view.groups.filter((g) => g.id !== "weapons")]
    : view.groups;
  const metered = viewGroups.map((g) => (g.id === "systems" ? { ...g, meter: { label: "SP", ...view.sp } } : g));
  const groups = editing.groups ? editing.groups(metered) : metered;

  return (
    <UnitSheet
      posture="build"
      className="unit-sheet-build is-mech"
      identity={{
        name: mech.name || "Unnamed mech",
        kindLine: [frameName ? (mfr ? `${frameName} // ${mfr}` : frameName) : "NO CHASSIS", `PILOT ${pilot.callsign || pilot.name}`, ...(playerName ? [playerName] : [])],
        templates: [],
        side: "pc",
        hidden: false,
      }}
      art={
        artUrl ? (
          // An uploaded portrait wins over the chassis art (`resolveMechArtUrl`); a pruned upload falls back to the placeholder.
          <AvatarImage
            src={artUrl}
            alt={mech.image ? "Mech portrait" : `${frameName ?? "mech"} chassis art`}
            fallback={<span className="unit-sheet-art-empty">{mech.image ? "NO ART" : "NO CHASSIS ART"}</span>}
          />
        ) : (
          <span className="unit-sheet-art-empty">NO CHASSIS</span>
        )
      }
      badges={
        <>
          {isActive && <span className="unit-sheet-badge">ACTIVE</span>}
          {editing.headerControls}
        </>
      }
      band={
        band || editing.prototypeBand ? (
          <>
            {band}
            {editing.prototypeBand}
          </>
        ) : undefined
      }
      actions={
        actions || editing.toggle ? (
          <>
            {actions}
            {editing.toggle}
          </>
        ) : undefined
      }
      nameEditor={editing.nameEditor}
      toolbar={editing.toolbar}
      error={editing.error}
    >
      {view.violations.length > 0 && (
        <div className="unit-sheet-hazard" role="status">
          {view.violations.map((v, i) => (
            <div key={`${v.kind}-${i}`}>
              <Icon icon={faTriangleExclamation} className="icon-lead" />
              {v.message}
            </div>
          ))}
        </div>
      )}
      <div className="unit-sheet-cols">
        <div className="unit-sheet-col">
          {view.vitals ? (
            <UnitSheetVitals
              hp={view.vitals.hp.value}
              maxHp={view.vitals.hp.max}
              heat={view.vitals.heat}
              exposed={false}
              structure={view.vitals.structure}
              stress={view.vitals.stress}
              repair={view.vitals.repair}
              shield={0}
              burn={0}
              onSetTrack={null}
              disabled
            />
          ) : (
            <div className="unit-sheet-section">
              <div className="unit-sheet-section-empty">No chassis registered: {edit?.editing ? "SELECT FRAME above." : "pick a frame in EDIT."}</div>
            </div>
          )}
          {view.stats && <StatStrip cells={view.stats} onOpenDice={onRoll} />}
          <StatStrip cells={view.hase} className="is-hase" onOpenDice={onRoll} />
          {editing.prototype && <SheetSection title="PROTOTYPE WEAPON">{editing.prototype}</SheetSection>}
          {wear}
          <SheetNotes notes={mech.notes} label="Mech notes" placeholder="Anything you want to remember about this mech: plans, reminders, in-character thoughts." onCommit={onCommitNotes} />
        </div>
        <div className="unit-sheet-col">
          <UnitLoadout groups={groups} onOpenRef={onOpenInCompendium} rowActions={editing.rowActions} sectionActions={editing.sectionActions} groupFoot={editing.groupFoot} />
        </div>
      </div>
      {editing.overlay}
    </UnitSheet>
  );
}
